import Editor from "./Editor";
import Invite from "./Invite";
import ProofreadingOrder from "./ProofreadingOrder";
import AssignmentViewHelper from "../../../components/Gantt/AssignmentViewHelper";
import AssignmentPool from "./AssignmentPool";
import Service from "./Service";
const moment = require('moment');

export default class Assignment {
    private readonly _id: string;
    private readonly _order: ProofreadingOrder;
    private readonly _name: string;
    private readonly _slug: string;
    private readonly _editingServicePart: number|null;
    private readonly _type: string;
    private readonly _wordCount: number;
    private readonly _size: number;
    private readonly _onHold: boolean;
    private readonly _editorDeadline: Date|null;
    private readonly _deadline: Date;
    private readonly _startDate: Date;
    private readonly _language: string;
    private readonly _services: Array<Service>;
    private readonly _status: string;
    private readonly _invites: Array<Invite>;
    private readonly _editor: Editor|null;
    private readonly _previousAssignment: Assignment;
    private readonly _nextAssignments: Array<Assignment>;
    private readonly _interrupted: boolean;
    private readonly _scope: string;
    private readonly _links: object;
    private readonly _canCreateSampleEdit: boolean;
    private readonly _canBeDeleted: boolean;
    private readonly _canCreateQualityCheck: boolean;
    private readonly _canSelectThumbnails: boolean;
    private readonly _scopeAutoGenerated: boolean;
    private readonly _rate: string|null;
    private readonly _canUpdateRate: boolean;
    private readonly _canBeSplit: boolean;
    private readonly _assignmentPools: AssignmentPool[];
    private readonly _editorDownloadedStudentDocument: Date|null;
    private readonly _hasEnqueuedAssignmentPool: boolean;

    private _contextual: boolean;
    private _viewHelper: AssignmentViewHelper;
    constructor({
        id,
        name,
        slug,
        editingServicePart,
        '@type': type,
        wordCount,
        size,
        onHold,
        editorDeadline,
        deadline,
        startDate,
        language,
        services,
        status,
        invites,
        editor,
        previousAssignment,
        nextAssignments,
        interrupted,
        scope,
        links,
        canCreateSampleEdit,
        canBeDeleted,
        canSelectThumbnails,
        scopeAutoGenerated,
        rate,
        canUpdateRate,
        canBeSplit,
        assignmentPools,
        contextual,
        editorDownloadedStudentDocument,
        waitingList,
        canCreateQualityCheck,
    }, order) {
        this._id = id;
        this._order = order;
        this._name = name;
        this._slug = slug;
        this._editingServicePart = editingServicePart;
        this._type = type;
        this._wordCount = wordCount;
        this._size = size;
        this._onHold = !!onHold;
        this._editorDeadline = editorDeadline ? new Date(editorDeadline) : null;
        this._deadline = new Date(deadline);
        this._language = language;
        this._services = services ? services.map(s => new Service(s)) : [];
        this._status = status;
        this._invites = (invites ?? []).map(i => new Invite(i, this)).sort((a, b) => (a.queuedAt > b.queuedAt) ? 1 : -1);
        // @ts-ignore
        this._editor = editor ? new Editor(editor, this._language) : null;
        this._previousAssignment = previousAssignment ?? null;
        this._nextAssignments = (nextAssignments ?? []).map(a => new Assignment(a, this._order));
        this._interrupted = interrupted;
        this._startDate = new Date(startDate);
        this._rate = rate;
        this._canUpdateRate = canUpdateRate;
        this._canBeSplit = canBeSplit;
        this._assignmentPools = (assignmentPools ?? []).map(a => new AssignmentPool(a)).sort((a, b) => a.createdAt < b.createdAt ? 1 : -1);
        this._contextual = !!contextual;
        this._scope = scope;
        this._links = links;
        this._canCreateSampleEdit = canCreateSampleEdit;
        this._canBeDeleted = canBeDeleted;
        this._canCreateQualityCheck = canCreateQualityCheck;
        this._canSelectThumbnails = canSelectThumbnails;
        this._scopeAutoGenerated = scopeAutoGenerated;
        this._editorDownloadedStudentDocument = editorDownloadedStudentDocument ? new Date(editorDownloadedStudentDocument) : null;
        this._hasEnqueuedAssignmentPool = waitingList ? waitingList.enqueued_assignment_pool : false;
        this._viewHelper = new AssignmentViewHelper();
    }

    get id(): string {
        return this._id;
    }

    get order(): ProofreadingOrder {
        return this._order;
    }

    get name(): string {
        return this._name;
    }

    get slug(): string {
        return this._slug;
    }

    get editingServicePart(): number | null {
        return this._editingServicePart;
    }

    get type(): string {
        return this._type;
    }

    get isSampleEdit(): boolean {
        return this._type === 'sampleEdit';
    }

    get isReferenceCheck(): boolean {
        return this._type === 'referenceCheck';
    }

    get isLayoutCheck(): boolean {
        return this._type === 'layoutCheck';
    }

    get isLayoutOrReferenceCheck(): boolean {
        return this.isLayoutCheck || this.isReferenceCheck;
    }
    get isSummary(): boolean {
        return this._type === 'summary';
    }

    get isEditingService(): boolean {
        return this._type === 'editingService';
    }

    get wordCount(): number {
        return this._wordCount;
    }

    get size(): number {
        return this._size;
    }

    get bundleAssignmentSizes(): Array<Service> {
            let sizes =[]

            if(this.isBundle) {
                this.services.forEach(s => {
                    // @ts-ignore
                    if(s.type === 'structure_check' || s.type === 'clarity_check' || s.type === 'gender_editing') return;
                    // @ts-ignore
                    sizes.push(s);
                });
            }
            return sizes;
    }

    get sizeUnit(): string {
        if (this.isReferenceCheck) {
            return 'references';
        }

        if (this.isLayoutCheck) {
            return 'pages';
        }

        return 'words';
    }

    get onHold(): boolean {
        return this._onHold;
    }

    get editorDeadline(): Date|null {
        return this._editorDeadline || this._deadline;
    }


    get ganttEditorDeadline(): Date {
        if (this._editorDeadline) {
            return this._editorDeadline;
        }

        let currentInvite = this._invites.find(i => i.isWaitingForReply);

        if (currentInvite) {
            return currentInvite.deadline;
        }

        return this._deadline;
    }

    get deadline(): Date {
        return this._deadline;
    }

    get startDate(): Date {
        return this._startDate;
    }

    get endDate(): Date {
        if (this._nextAssignments[0]) {
            return this._nextAssignments[0].startDate;
        }

        return this._deadline;
    }

    get language(): string {
        return this._language;
    }

    get services(): Array<Service> {
        return this._services;
    }

    get status(): string {
        return this._status;
    }

    get isCompleted(): boolean {
        return this._status === 'completed';
    }


    get isDelivered(): boolean {
        return this._status === 'delivered';
    }

    get isCompletedOrHigher(): boolean {
        return this.isDelivered || this.isCompleted;
    }

    get isNew(): boolean {
        return this._status === 'new';
    }

    get isAccepted(): boolean {
        return this._status === 'accepted';
    }

    get isPlanned(): boolean {
        if(!this.previousAssignment) {
            return false;
        }

        const previous = this.order.assignments.filter(a => a.id === this.previousAssignment.id)[0];
        if(!previous.isCompletedOrHigher) {
            return true;
        }

        if(this.onHold) {
            return true;
        }

        return false;
    }

    get canBePutOnHold(): boolean {
        return this._status !== 'completed' && this._status !== 'delivered';
    }

    get hasOpenWaitingList(): boolean {
        return this._status === 'new' && this._order.isCancelled() === false;
    }

    get canBeReopened(): boolean {
        return this.isCompletedOrHigher;
    }

    get canDisconnectEditor(): boolean {
        // @ts-ignore
        return this._editor && !this.isCompletedOrHigher;
    }

    get canBeInterrupted(): boolean {
        return this.hasLink('interrupt');
    }

    get canBeDeleted(): boolean {
        return this._canBeDeleted && this._order.assignments.length > 1;
    }

    get canSelectThumbnails(): boolean {
        return this._canSelectThumbnails;
    }

    get scopeAutoGenerated(): boolean {
        return this._scopeAutoGenerated;
    }

    get canCreateSampleEdit(): boolean {
        return this._canCreateSampleEdit;
    }

    get canBeSplit(): boolean {
        return this._canBeSplit;
    }

    get canBeDelivered(): boolean {
        return this._onHold === false && this._status === 'completed' && this.nextAssignments.length === 0;
    }

    get canBeDeliveredIntermediately(): boolean {
        return this._onHold === false && this._status === 'completed' && this.nextAssignments.length !== 0;
    }

    get ganttStatus(): string {
        let now = (new Date()).getTime();
        if (now > this.ganttEditorDeadline.getTime() && this._status !== 'completed' && this._status !== 'delivered' && this._order.isCancelled() === false) {
            return 'overdue';
        }

        if (this.isNew && this.activeAssignmentPool) {
            return this.hasStaleAssignmentPool ? 'stale-assignment-pool' : 'assignment-pool';
        }

        // @ts-ignore
        if (this._invites.find((i) => i.status === 'waiting-for-reply')) {
            return 'waiting-for-reply';
        }

        return this._status;
    }

    get activeAssignmentPool(): AssignmentPool|undefined {
        return this._assignmentPools.find(a => a.isActive);
    }

    get editor(): Editor|null {
        return this._editor;
    }

    get invites(): Array<Invite> {
        return this._invites;
    }

    get hasInvites(): boolean {
        return this._invites.length !== 0;
    }

    get interrupted(): boolean {
        return this._interrupted;
    }

    get isQualityCheck(): boolean {
        return this._slug === 'qualityCheck';
    }

    get isBundle(): boolean {
        return this._slug === 'bundle';
    }

    get scope(): string {
        return this._scope;
    }

    get links(): object {
        return this._links;
    }

    get rate(): string|null {
        return this._rate;
    }

    get hasEnqueuedAssignmentPool(): boolean {
        return this._hasEnqueuedAssignmentPool;
    }

    get canUpdateRate(): boolean {
        return this._canUpdateRate;
    }

    get contextual(): boolean {
        return this._contextual;
    }

    set contextual(value) {
        this._contextual = value;
    }

    get previousAssignment(): Assignment {
        return this._previousAssignment;
    }

    get nextAssignments(): Array<Assignment> {
        return this._nextAssignments;
    }

    get hasNextAssignment(): boolean {
        return this._nextAssignments.length !== 0;
    }

    get hasQualityCheck(): boolean {
        return !!this._order.assignments.find(a => a.previousAssignment && a.previousAssignment.id === this._id && a.isQualityCheck);
    }

    get hasLayoutCheck(): boolean {
        // @ts-ignore
        return this._services.find(s => s.type === 'layout_check') !== undefined;
    }

    get hasStructureCheck(): boolean {
        // @ts-ignore
        return this._services.find(s => s.type === 'structure_check') !== undefined;
    }

    get hasClarityCheck(): boolean {
        // @ts-ignore
        return this._services.find(s => s.type === 'clarity_check') !== undefined;
    }

    get hasGenderEditing() {
        // @ts-ignore
        return this._services.find(s => s.type === 'gender_editing') !== undefined;
    }

    get hasEssayEditing() {
        // @ts-ignore
        return this._services.find(s => s.type === 'essay_editing_and_coaching') !== undefined;
    }

    get hasExpertReview() {
        // @ts-ignore
        return this._services.find(s => s.type === 'expert_review') !== undefined;
    }

    get hasReferenceCheck(): boolean {
        // @ts-ignore
        return this._services.find(s => s.type === 'reference_check') !== undefined;
    }

    get invitedAt(): Date|null {
        let invite = this._invites.find(i => i.isAccepted || i.isWaitingForReply);

        if (invite) {
            return invite.invitedAt;
        }

        return null;
    }

    get canCreateQualityCheck() {
        return this._canCreateQualityCheck;
    }

    get editorDownloadedStudentDocument(): Date | null {
        return this._editorDownloadedStudentDocument;
    }

    get viewHelper(): { offset: number } {
        return this._viewHelper;
    }

    hasLink(name) {
        return !!this.links[name];
    }

    get assignmentPools(): AssignmentPool[] {
        return this._assignmentPools;
    }

    get canCreateAssignmentPool() : boolean {
        return !(this.invites.find(i => i.isWaitingForReply) || this.editor);
    }

    get hasWaitingListWithOpenInvites() : boolean {
        return !!(this.invites.find(i => i.isWaitingForReply) || this.editor);
    }

    get hasStaleAssignmentPool() : boolean {
        if (!this.activeAssignmentPool) { return false }
        const timeDifference = moment.duration(moment().diff(moment(this.activeAssignmentPool.createdAt)));
        return (this._order.turnaround === 8 || this._order.turnaround === 9) ? timeDifference.asMinutes() > 30 : timeDifference.asMinutes() > 120;
    }

    get hasCorrespondingSampleEdit(): boolean {
        return !!this.order.assignments.find(a => a.type === 'sampleEdit');
    }

    get bundleAssignmentServices(): Array<object> {
        let services = [];

        if(this.isBundle) {
            // @ts-ignore
            services.unshift({name: `${this._name}:`});

            this.services.forEach(s => {
                // @ts-ignore
                if(s.type === 'proofreading_and_editing') {
                    // @ts-ignore
                    services.push(new Service({
                        language: s.language,
                        name: 'Editing Service',
                        size: s.size,
                        sizeUnit: s.sizeUnit,
                        type: s.type,
                    }));
                    return;
                }
                // @ts-ignore
                if(s.type === 'structure_check' || s.type === 'clarity_check' || s.type === 'gender_editing') {
                    return;
                }
                // @ts-ignore
                services.push(s);
            });
        }
        return services;
    }
}
